import { useState, useContext } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Link from 'next/link';
import { UserContext } from '../components/Navigation';
import Icon from '../components/Icon';
import { display } from '../lib/fonts';
import { page as pageBg, card, btn, badge, body, input as inputCls } from '../lib/ui';

export default function BrowseJobs({ initialJobs, initialTotalPages, initialPage }) {
  const router = useRouter();
  const { user } = useContext(UserContext);
  const [jobs, setJobs] = useState(initialJobs || []);
  const [page, setPage] = useState(initialPage || 1);
  const [totalPages, setTotalPages] = useState(initialTotalPages || 1);
  const [searchTerm, setSearchTerm] = useState(router.query.q || '');
  const [selectedType, setSelectedType] = useState(router.query.type || '');
  const [selectedLocation, setSelectedLocation] = useState(router.query.location || '');

  const handleSearch = (e) => {
    e.preventDefault();
    router.push({
      pathname: '/browse-jobs',
      query: { q: searchTerm, type: selectedType, location: selectedLocation, page: 1 },
    });
  };

  const handlePageChange = (newPage) => {
    router.push({
      pathname: '/browse-jobs',
      query: { ...router.query, page: newPage },
    });
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": jobs.map((job, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "url": `https://doogler.in/jobs/${job.jobId || job._id}`
    }))
  };

  return (
    <div className={pageBg}>
      <Head>
        <title>Browse Referral Jobs | Find Openings in Top Companies | Doogler</title>
        <meta name="description" content="Explore job openings posted by employees at top tech companies. Get a referral and boost your chances of landing your dream job." />
        <meta name="keywords" content="referral jobs, tech jobs india, hiring now, employee referral portal, software engineer jobs, MNC jobs" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
        />
      </Head>

      <main className={`mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-10 sm:py-12`}>
        <header className="mb-8">
          <h1 className={`${display.className} text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white`}>Browse Jobs</h1>
          <p className={`mt-2 ${body}`}>Exclusive referral opportunities from our internal network.</p>
        </header>

        <form onSubmit={handleSearch} className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto]">
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-slate-400">
              <Icon name="search" className="w-5 h-5" strokeWidth={2} />
            </span>
            <label htmlFor="job-q" className="sr-only">Search jobs</label>
            <input
              id="job-q"
              type="text"
              inputMode="search"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${inputCls} pl-11`}
            />
          </div>
          <div>
            <label htmlFor="job-type" className="sr-only">Job type</label>
            <select
              id="job-type"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className={`${inputCls} sm:min-w-[160px]`}
            >
              <option value="">All Types</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Contract">Contract</option>
              <option value="Internship">Internship</option>
            </select>
          </div>
          <button type="submit" className={`${btn.primary} min-h-[48px] sm:px-8`}>Filter</button>
        </form>

        {jobs.length === 0 ? (
          <div className={`${card} px-6 py-16 text-center`}>
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 dark:bg-gray-800 text-slate-400">
              <Icon name="search" className="w-6 h-6" />
            </span>
            <h3 className="mt-4 text-xl font-semibold text-slate-900 dark:text-white">No Jobs Found</h3>
            <p className={`mx-auto mt-2 max-w-sm ${body}`}>
              We couldn&apos;t find any jobs matching your criteria. Try adjusting your filters or check back later!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <article
                key={job._id}
                className={`${card} group p-5 sm:p-6 transition hover:border-customBlue/50`}
              >
                <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      {job.type && <span className={badge.brand}>{job.type}</span>}
                      {job.salary && <span className={badge.success}>{job.salary}</span>}
                    </div>
                    <h2 className="text-xl sm:text-2xl font-semibold leading-snug text-slate-900 dark:text-white">
                      <Link href={`/jobs/${job.jobId || job._id}`} className="transition-colors group-hover:text-teal-700 dark:group-hover:text-customBlue">
                        {job.title}
                      </Link>
                    </h2>
                    <p className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-slate-600 dark:text-slate-400">
                      <span className="inline-flex items-center gap-1.5"><Icon name="building" className="w-4 h-4 text-slate-400" />{job.company}</span>
                      <span className="inline-flex items-center gap-1.5"><Icon name="pin" className="w-4 h-4 text-slate-400" />{job.location}</span>
                    </p>
                    <p className="mt-3 line-clamp-2 text-sm sm:text-base leading-relaxed text-slate-500 dark:text-slate-400">
                      {job.description}
                    </p>
                  </div>
                  <div className="flex w-full flex-col gap-2 sm:flex-row md:w-auto md:flex-col lg:flex-row">
                    <Link href={`/jobs/${job.jobId || job._id}`} className={btn.secondary}>
                      View Details
                    </Link>
                    <Link href={`/jobs/${job.jobId || job._id}`} className={btn.primary}>
                      Apply Now
                    </Link>
                  </div>
                </div>
              </article>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <nav aria-label="Pagination" className="flex items-center justify-center gap-4 pt-8">
                <button
                  onClick={() => handlePageChange(Math.max(1, page - 1))}
                  disabled={page === 1}
                  aria-label="Previous page"
                  className={`${btn.secondary} min-w-[44px] px-3`}
                >
                  <Icon name="arrowLeft" className="w-5 h-5" />
                </button>
                <p className="text-base font-medium text-slate-900 dark:text-white">
                  {page} <span className="text-slate-400">of</span> {totalPages}
                </p>
                <button
                  onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  aria-label="Next page"
                  className={`${btn.secondary} min-w-[44px] px-3`}
                >
                  <Icon name="arrow" className="w-5 h-5" />
                </button>
              </nav>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export async function getServerSideProps(context) {
  const { q = '', type = '', location = '', page = 1 } = context.query;
  const limit = 10;
  const skip = (parseInt(page) - 1) * limit;

  try {
    const connectToDatabase = (await import('../lib/mongodb')).default;
    const mongoose = (await import('mongoose')).default;
    
    await connectToDatabase();
    const db = mongoose.connection.db;
    const jobsCollection = db.collection('jobs');

    const filter = {};
    if (q) {
      filter.$or = [
        { title: { $regex: q, $options: 'i' } },
        { company: { $regex: q, $options: 'i' } },
        { description: { $regex: q, $options: 'i' } }
      ];
    }
    if (type) filter.type = type;
    if (location) filter.location = { $regex: location, $options: 'i' };

    const totalJobs = await jobsCollection.countDocuments(filter);
    const jobs = await jobsCollection.find(filter)
      .sort({ createdAt: -1 })
      .skip(skip)
      .limit(limit)
      .toArray();

    return {
      props: {
        initialJobs: JSON.parse(JSON.stringify(jobs)),
        initialTotalPages: Math.ceil(totalJobs / limit) || 1,
        initialPage: parseInt(page)
      }
    };
  } catch (error) {
    console.error('Error fetching jobs in BrowseJobs SSR:', error);
    return {
      props: {
        initialJobs: [],
        initialTotalPages: 1,
        initialPage: 1
      }
    };
  }
}
